"use client";

import { Trans } from "@lingui/react/macro";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AVAILABLE_MODELS,
  type ModelId,
  type ModelProviderId,
} from "@/lib/ai/models";
import type { ChatQuotaTierIndicatorState } from "./chat-quota-view-state";
import { ModelProviderIcon } from "./model-provider-icon";
import { ModelSelectOptionLabel } from "./model-select-option-label";
import { ModelSelectTrigger } from "./model-select-trigger";

const MODEL_FAMILIES = [
  { provider: "openai", label: "GPT" },
  { provider: "anthropic", label: "Claude" },
  { provider: "google", label: "Gemini" },
] as const satisfies ReadonlyArray<{
  provider: ModelProviderId;
  label: string;
}>;

export const MODEL_FAMILY_ORDER = MODEL_FAMILIES.map(
  ({ provider }) => provider,
);

type Props = {
  model: ModelId;
  quotaState: ChatQuotaTierIndicatorState;
  disabled?: boolean;
  onOpenChange: (open: boolean) => void;
  onValueChange: (modelId: ModelId) => void;
};

/** Groups the growing model catalog by the product families users recognize. */
export function ModelSelectMenu({
  model,
  quotaState,
  disabled,
  onOpenChange,
  onValueChange,
}: Props) {
  const selectedModel = AVAILABLE_MODELS.find(
    (availableModel) => availableModel.value === model,
  );

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <ModelSelectTrigger
          model={model}
          quotaState={quotaState}
          disabled={disabled}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={6}
        className="w-56"
      >
        <DropdownMenuLabel className="pb-1 text-xs font-medium text-muted-foreground">
          <Trans>AI model</Trans>
        </DropdownMenuLabel>
        {MODEL_FAMILIES.map((family) => {
          const familyModels = AVAILABLE_MODELS.filter(
            (availableModel) => availableModel.provider === family.provider,
          );
          const selectedInFamily = selectedModel?.provider === family.provider;

          return (
            <DropdownMenuSub key={family.provider}>
              <DropdownMenuSubTrigger className="min-h-11 py-2">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
                  <ModelProviderIcon provider={family.provider} />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="font-medium leading-none">
                    {family.label}
                  </span>
                  <span className="truncate text-[11px] leading-none text-muted-foreground">
                    {selectedInFamily ? (
                      selectedModel.label
                    ) : (
                      <Trans>{familyModels.length} models</Trans>
                    )}
                  </span>
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent sideOffset={6} className="w-72 p-1.5">
                <DropdownMenuLabel className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
                  <ModelProviderIcon provider={family.provider} />
                  <span>{family.label}</span>
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={model}
                  onValueChange={(value) => onValueChange(value as ModelId)}
                >
                  {familyModels.map((availableModel) => (
                    <DropdownMenuRadioItem
                      key={availableModel.value}
                      value={availableModel.value}
                      className="min-h-9 py-2 pr-2"
                    >
                      <ModelSelectOptionLabel
                        modelId={availableModel.value}
                        label={availableModel.label}
                      />
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
